<template>
    <Card :title="$t('labels.create_product')">
        <div class="card-body">
            <div class="row">
                <div class="col-lg-6">
                    <Fieldset :title="$t('labels.images')">
                        <draggable
                            v-model="product.images"
                            class="image-gallery"
                            :item-key="item => item"
                        >
                            <template #item="{ element, index }">
                                <div class="image-item">
                                    <img :src="element">
                                    <div class="image-actions">
                                        <button @click="openImage(element)" class="image-action-open-image" :title="$t('labels.open_image_in_new_tab')">
                                            <i class="icon-eye"></i>
                                        </button>
                                        <button @click="removeImage(element)" :title="$t('labels.remove_image')">
                                            <i class="icon-cross3"></i>
                                        </button>
                                    </div>
                                    <div class="drag-handle">
                                        <i class="icon-move"></i>
                                    </div>
                                    <div class="common-button main-image-badge" v-if="!index">{{ $t('labels.main_image') }}</div>
                                    <button class="common-button make-main-btn" v-else @click="makeMainImage(element)">{{ $t('labels.make_main') }}</button>
                                </div>
                            </template>
                        </draggable>
                        <div
                            class="drop-zone mb-3"
                            @click="$refs.File.showFile()"
                            @dragover.prevent
                            @dragenter.prevent
                            @drop.prevent="handleFileDrop"
                        >
                            <i class="icon-cloud-upload fs-24"></i>
                            <div class="mt-2">
                                <p>{{$t('labels.drag_and_drop') }}
                                    <span class="text-primary">
                                        {{ $t('labels.browse') }}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </Fieldset>
                    <Fieldset :title="$t('labels.main_information')">
                        <div class="row">
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.category')">
                                    <AppAutoComplete
                                        :model-value="product.category?.uuid"
                                        :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/categories`"
                                        @update:model-value="changeCategory"
                                        :selected-value="getAvailableTranslations(product.category?.names)"
                                    >
                                        <template #optionContent>
                                            {{ getAvailableTranslations(product.category?.names) ?? $t('labels.empty') }}
                                        </template>
                                        <template #optionItem="{option, index}">
                                            {{ getAvailableTranslations(option.names) }}
                                        </template>
                                    </AppAutoComplete>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.sku')">
                                    <Input v-model="product.sku"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-6">
                                <FormGroup :label="$t('labels.measurement')">
                                    <Input v-model="product.measurement"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.manufacturer')">
                                    <Input v-model="product.manufacturer"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.balance')">
                                    <Input v-model="product.balance" type="number"/>
                                </FormGroup>
                            </div>
                        </div>
                    </Fieldset>
                    <Fieldset :title="$t('labels.filters')">
                        <template v-if="allFilters.length">
                            <div class="row">
                                <div class="col-lg-4" v-for="(filter, filterIndex) in allFilters" :key="filter.uuid">
                                    <FormGroup :label="getAvailableTranslations(filter.names) + (filter.measurement_unit ? ` (${filter.measurement_unit})` : '')">
                                        <AppAutoComplete
                                            v-if="!filter.is_multiple"
                                            :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filter-items`"
                                            :model-value="selectedFilters[filter.uuid]?.items[0]?.uuid"
                                            :selected-value="getAvailableTranslations(selectedFilters[filter.uuid]?.items[0]?.names)"
                                            :has-create-button="true"
                                            :can-be-empty="true"
                                            @click:create="$refs.CreateIntegrationFilterItemModal.show(filterIndex, null, filter)"
                                            @update:model-value="selectFilter(filter, $event)"
                                            :selected-values="getSelectedIntegrationFilterItemUuids(filter)"
                                            :queryParams="{
                                                integration_filter_uuids: [filter.uuid]
                                            }"
                                            :disabled="filter.default_items.length > 0"
                                        >
                                            <template #optionContent v-if="selectedFilters[filter.uuid]">
                                                {{ getAvailableTranslations(selectedFilters[filter.uuid].items?.[0]?.names) }}
                                            </template>
                                            <template #optionItem="{option, index}">
                                                {{ getAvailableTranslations(option.names) }}
                                            </template>
                                        </AppAutoComplete>
                                        <MultiAutoComplete
                                            v-else
                                            :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filter-items`"
                                            :model-value="selectedFilters[filter.uuid]?.items || []"
                                            :has-create-button="true"
                                            :can-be-empty="true"
                                            @update:model-value="selectFilter(filter, $event)"
                                            @click:create="$refs.CreateIntegrationFilterItemModal.show(filterIndex, null, filter)"
                                            :queryParams="{
                                                integration_filter_uuids: [filter.uuid]
                                            }"
                                            :disabled="filter.default_items.length > 0"
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                        </template>
                    </Fieldset>
                    <Fieldset :title="$t('labels.prices')">
                        <div class="row">
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.price')">
                                    <Input
                                        v-model="product.price"
                                        type="number"
                                    />
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.sale_price')">
                                    <Input
                                        v-model="product.sale_price"
                                        type="number"
                                    />
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.promo_price')">
                                    <Input
                                        v-model="product.promo_price"
                                        type="number"
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </Fieldset>
                    <Fieldset :title="$t('labels.identifiers')">
                        <div class="row">
                            <div class="col-md-4">
                                <FormGroup :label="`${$t('labels.external_male')} (${$t('labels.main')})`">
                                    <Input v-model="product.source_id"/>
                                </FormGroup>
                            </div>
                            <div class="col-md-4">
                                <FormGroup :label="`${$t('labels.external_male')} (${$t('labels.secondary')})`">
                                    <Input v-model="product.second_source_id"/>
                                </FormGroup>
                            </div>
                            <div class="col-md-4">
                                <FormGroup :label="$t('labels.group_id')">
                                    <Input v-model="product.group_key"/>
                                </FormGroup>
                            </div>
                        </div>
                    </Fieldset>
                    <Fieldset :title="$t('labels.settings')">
                        <FormGroup :label="$t('labels.exportEnabled')">
                            <AppCheckbox v-model="product.export_enabled"/>
                        </FormGroup>
                    </Fieldset>
                </div>
                <div class="col-lg-6">
                    <Fieldset :title="$t('labels.lang_content')">
                        <NavTab class="nav-tabs-highlight">
                            <NavTabItem
                                v-for="(language, index) in integration.language_aliases"
                                :key="language" :href="`#${language}`"
                                :ref="el => itemRefs[index] = el"
                            >
                                {{ $t(`languages.${language}`) }}
                            </NavTabItem>
                            <template #content>
                                <NavTabPane v-for="language in integration.language_aliases" :id="`${language}`">
                                    <FormGroup :label="$t('labels.name')">
                                        <Input v-model="product.names[language]"/>
                                    </FormGroup>
                                    <FormGroup :label="$t('labels.descriptions')">
                                        <InputTextArea v-model="product.descriptions[language]"/>
                                    </FormGroup>
                                    <FormGroup :label="$t('labels.seos')">
                                        <Tags v-model="product.seos[language]" :disabledTags="categoryTags[language]"/>
                                    </FormGroup>
                                </NavTabPane>
                            </template>
                        </NavTab>
                    </Fieldset>
                </div>
            </div>
        </div>
        <div class="card-footer d-flex justify-content-end">
            <AppButton color="indigo" @click="createProduct" :disabled="!productCanBeCreated || creationStarted">
                {{ $t('buttons.create') }}
            </AppButton>
        </div>
    </Card>
    <File ref="File" @fileIsSelected="selectFile" :accept="['image/*']"/>
    <CreateIntegrationFilterItemModal
        ref="CreateIntegrationFilterItemModal"
        :integration="integration"
        :external-category="product.category"
        @created="({filterItem, filterIndex}) => {
            selectFilter(allFilters[filterIndex], filterItem)
        }"
    />
</template>
<script>
import Card from "../../../../components/new/CardComponent.vue";
import Fieldset from "../../../../components/new/AppFieldset.vue";
import File from '../../../../components/new/file/AppFile.vue';
import NavTab from "../../../components/tabs/NavTab.vue";
import NavTabItem from "../../../components/tabs/NavTabItem.vue";
import NavTabPane from "../../../components/tabs/NavTabPane.vue";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../components/new/form/Input.vue";
import AppAutoComplete from "../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import MultiAutoComplete from "../../../../components/new/form/autocomplete/MultiAutoComplete.vue";
import InputTextArea from "../../../../components/new/form/InputTextArea.vue";
import Tags from "../../../../components/new/form/Tags.vue";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import AppButton from "../../../../components/new/button/AppButton.vue";
import CreateIntegrationFilterItemModal from "../modals/CreateIntegrationFilterItemModal.vue";

import draggable from 'vuedraggable';
import { httpClient } from "../../../../services/HttpClient";
import MessageNotificator from "../../../../services/MessageNotificator";

export default {
    name: "CreateProductCard",
    components: {
        Card, Fieldset, draggable, File, NavTab, NavTabItem, NavTabPane, FormGroup, Input, AppAutoComplete,
        MultiAutoComplete, InputTextArea, Tags, AppCheckbox, AppButton, CreateIntegrationFilterItemModal
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            product: {
                images: [],
                names: {},
                category: null,
                descriptions: {},
                seos: {},
                sku: null,
                measurement: null,
                manufacturer: null,
                balance: null,
                price: null,
                sale_price: null,
                promo_price: null,
                source_id: null,
                second_source_id: null,
                group_key: null,
                export_enabled: false,
            },
            selectedFilters: {},
            allFilters: [],
            itemRefs: [],
            creationStarted: false
        }
    },
    methods: {
        integrationFilterItemCreated(filterItem, filterIndex) {
            new MessageNotificator().success(this.$t('alerts.success'));
            this.selectFilter(this.allFilters[filterIndex], filterItem);
        },
        handleFileDrop(event) {
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.selectFile(files);
            }
        },
        async selectFile(files) {
            let responseData = [];
            let uploadPromises = Array.from(files).map((file) => {
                let requestFile = new FormData();
                requestFile.append('file', file);
                requestFile.append('type', 'product-item');
                return httpClient.post('/file/files', requestFile);
            });

            let responses = await Promise.all(uploadPromises);
            responseData = responses.map(response => response.data.data);

            this.product.images.push(...responseData.map(file => file.url));
        },
        getAvailableTranslations(objectWithTranslations) {
            if (!objectWithTranslations) {
                return null
            }
            return objectWithTranslations[this.mainLanguageAlias] ?? Object.values(objectWithTranslations)[0];
        },
        async changeCategory(category) {
            this.selectedFilters = {};
            this.product.category = category;
            await this.fetchAllFilters();
            this.selectDefaultFilterItems();
        },
        async fetchAllFilters() {
            const limit = 100;
            const filters = [];

            const firstResponse = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                integration_category_uuids: [this.product.category?.uuid],
                extra_fields: ['defaultItems'],
                page: 1,
                limit
            });
            filters.push(...firstResponse.data.data);

            const totalPages = Math.ceil(firstResponse.data.meta.total / limit);
            for (let page = 2; page <= totalPages; page++) {
                const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                    integration_category_uuids: [this.product.category?.uuid],
                    extra_fields: ['defaultItems'],
                    page,
                    limit
                });
                filters.push(...response.data.data);
            }

            this.allFilters = this.sortFiltersByName(filters);
        },
        sortFiltersByName(filters) {
            return filters.sort((a, b) => {
                const nameA = this.getAvailableTranslations(a.names)?.toLowerCase() || '';
                const nameB = this.getAvailableTranslations(b.names)?.toLowerCase() || '';

                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            });
        },
        selectDefaultFilterItems() {
            this.allFilters.forEach((filter) => {
                if (filter.default_items.length > 0) {
                    if (filter.is_multiple) {
                        this.selectFilter(filter, filter.default_items);
                        return;
                    }

                    this.selectFilter(filter, filter.default_items[0]);
                }
            });
        },
        getSelectedIntegrationFilterItemUuids(filter) {
            if (this.selectedFilters[filter.uuid]) {
                return this.selectedFilters[filter.uuid].items.map(item => item.uuid);
            }

            return [];
        },
        selectFilter(filter, filterItem) {
            const filterUuid = filter.uuid;
            if (!filterItem || (filter.is_multiple && filterItem.length === 0)) {
                delete this.selectedFilters[filterUuid];
                return;
            }

            if (filter.is_multiple) {
                let items = this.selectedFilters[filterUuid]?.items || [];

                if (!items.find(item => item.uuid === filterItem.uuid)) {
                    items = filterItem;
                }

                this.selectedFilters[filterUuid] = {
                    items: items
                };
            } else {
                this.selectedFilters[filterUuid] = {
                    items: [filterItem]
                };
            }
        },
        async createProduct() {
            this.creationStarted = true;

            let requestData = {
                names: Object.fromEntries(
                    Object.entries(this.product.names).filter(([_, value]) => value)
                ),
                integration_category_uuid: this.product.category.uuid,
                integration_uuid: this.integration.uuid,
            };

            if (this.product.images.length) {
                requestData.images = this.product.images;
            }

            if (Object.values(this.product.descriptions).filter(Boolean).length) {
                requestData.descriptions = Object.fromEntries(
                    Object.entries(this.product.descriptions).filter(([_, value]) => value)
                );
            }

            if (Object.values(this.product.seos).filter(Boolean).length) {
                requestData.seos = Object.fromEntries(
                    Object.entries(this.product.seos).filter(([_, value]) => value)
                );
            }

            if (this.product.sku) {
                requestData.sku = this.product.sku;
            }

            if (this.product.measurement) {
                requestData.measurement = this.product.measurement;
            }

            if (this.product.manufacturer) {
                requestData.manufacturer_name = this.product.manufacturer;
            }

            if (this.product.balance) {
                requestData.balance = this.product.balance;
            }

            if (this.product.price) {
                requestData.price = this.product.price;
            }

            if (this.product.sale_price) {
                requestData.sale_price = this.product.sale_price;
            }

            if (this.product.promo_price) {
                requestData.promo_price = this.product.promo_price;
            }

            if (this.product.source_id) {
                requestData.source_id = this.product.source_id;
            }

            if (this.product.second_source_id) {
                requestData.second_source_id = this.product.second_source_id;
            }

            if (this.product.group_key) {
                requestData.group_key = this.product.group_key;
            }

            if (Object.keys(this.selectedFilters).length) {
                requestData.filter_item_uuids = Object.values(this.selectedFilters).reduce((acc, filter) => {
                    acc.push(...filter.items.map(item => item.uuid));

                    return acc;
                }, []);
            }

            requestData.export_enabled = this.product.export_enabled;

            await httpClient.post('/integrations/products', requestData).then(() => {
                this.$router.push({ name: 'IntegrationProductsTable' })
            });

            this.creationStarted = false;
        }
    },
    computed: {
        mainLanguageAlias() {
            return this.integration.language_aliases[0]
        },
        categoryTags() {
            if (!this.product?.category?.seo_keywords) {
                return {}
            }
            return Object.entries(this.product.category.seo_keywords).reduce((acc, [languageAlias, tags]) => {
                acc[languageAlias] = tags.split(', ').map((tag) => ({
                    tag: tag,
                    reason: translate("labels.category")
                }))

                return acc
            }, {})
        },
        productCanBeCreated() {
            return Object.values(this.product.names).filter(Boolean).length && this.product.category
        }
    },
    updated() {
        this.itemRefs = []
    },
    async mounted() {
        this.itemRefs[0].click()
    }
}
</script>
<style scoped>
.image-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 15px;
}
.image-item {
    position: relative;
    width: 150px;
    height: 120px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    overflow: hidden;
}

.image-actions {
    position: absolute;
    top: 5px;
    right: 5px;
    display: flex;
    gap: 5px;
    transition: opacity 0.2s ease-in-out;
}
.image-action-open-image {
    opacity: 0;
}
.image-item:hover .image-actions .image-action-open-image {
    opacity: 1;
}
.image-actions button {
    width: 24px;
    height: 24px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.8);
    border: none;
    border-radius: 50%;
    font-size: 12px;
}
button:focus {
    outline: none;
}
.common-button {
    position: absolute;
    bottom: 5px;
    right: 5px;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 3px;
}
.main-image-badge {
    background-color: rgba(13, 110, 253, 0.9);
    color: white;
    z-index: 1;
}
.make-main-btn {
    background-color: rgba(255, 255, 255, 0.8);
    border: none;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
}
.image-item:hover .make-main-btn {
    opacity: 1;
}
.image-item:active {
    cursor: grabbing;
}
.drag-handle {
    position: absolute;
    bottom: 5px;
    left: 5px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    cursor: move;
    font-size: 12px;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
}
.image-item:hover .drag-handle {
    opacity: 1;
}
.drop-zone {
    border: 2px dashed #dee2e6;
    border-radius: 5px;
    padding: 20px;
    text-align: center;
    background-color: #f8f9fa;
    cursor: pointer;
    transition: background-color 0.3s;
}
.drop-zone:hover {
    background-color: #f2f1f0;
}
.message > span > i {
    font-size: 24px;
}
.modal-footer:not([class*=bg-]) {
    padding-top: 20px;
}
.fs-24 {
    font-size: 24px;
}
.gap-2 {
    gap: 0.5rem;
}
</style>
